import { Modal, Form, Input, Button, Space, Typography, Row, Col, Pagination } from 'antd';
import { useCreate, useNavigation, useList } from '@refinedev/core';
import { IEnrollment } from 'interfaces/enrollments';
import { useCallback, useMemo, useState } from 'react';

interface CreateDocumentationModalProps {
  open: boolean;
  onClose: (open: boolean) => void;
}

interface SearchFilters {
  search_user_name?: string;
  by_cpf?: string;
  by_user_email?: string;
}

export const CreateDocumentationModal = ({ open, onClose }: CreateDocumentationModalProps) => {
  const [form] = Form.useForm();
  const { edit } = useNavigation();
  const [searchFilters, setSearchFilters] = useState<SearchFilters>({});
  const [shouldFetch, setShouldFetch] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [creatingId, setCreatingId] = useState<string | null>(null);

  const { data: enrollmentsResponse, isLoading, refetch } = useList<IEnrollment>({
    resource: 'graduate/courses/enrollments',
    filters: [
      {
        field: 'filter[without_documentation]',
        operator: 'eq' as const,
        value: true
      },
      ...(searchFilters.search_user_name
        ? [{ field: 'filter[search_user_name]', operator: 'eq' as const, value: searchFilters.search_user_name as string }]
        : []),
      ...(searchFilters.by_cpf
        ? [{ field: 'filter[by_cpf]', operator: 'eq' as const, value: searchFilters.by_cpf as string }]
        : []),
      ...(searchFilters.by_user_email
        ? [{ field: 'filter[by_user_email]', operator: 'eq' as const, value: searchFilters.by_user_email as string }]
        : [])
    ],
    pagination: { current: currentPage, pageSize: 10 },
    queryOptions: {
      enabled: shouldFetch
    }
  });

  const enrollments = useMemo(() => (shouldFetch ? enrollmentsResponse?.data ?? [] : []), [enrollmentsResponse, shouldFetch]);

  const { mutateAsync: createDocumentation } = useCreate();

  const handleSearch = useCallback(async () => {
    const values = form.getFieldsValue();
    const nextFilters: SearchFilters = {
      search_user_name: values.search_user_name?.trim(),
      by_cpf: values.by_cpf?.trim(),
      by_user_email: values.by_user_email?.trim()
    };
    setSearchFilters(nextFilters);
    setShouldFetch(true);
    setCurrentPage(1);
    await refetch();
  }, [form, refetch]);

  const handleAdd = useCallback(
    async (enrollmentId: string) => {
      try {
        setCreatingId(enrollmentId);
        const response = await createDocumentation({
          resource: 'documentations',
          values: { enrollment_id: enrollmentId },
          successNotification: () => ({
            type: 'success',
            description: 'Sucesso!',
            message: 'Documentação criada com sucesso'
          }),
          errorNotification: () => ({
            type: 'error',
            description: 'Erro!',
            message: 'Não foi possível criar a documentação.'
          })
        });

        const createdId = response?.data?.id as string | undefined;
        if (createdId) {
          onClose(false);
          form.resetFields();
          setSearchFilters({});
          setShouldFetch(false);
          edit('documentations', createdId);
        }
      } finally {
        setCreatingId(null);
      }
    },
    [createDocumentation, edit, onClose, form]
  );

  const handleCancel = useCallback(() => {
    form.resetFields();
    setSearchFilters({});
    setShouldFetch(false);
    setCurrentPage(1);
    onClose(false);
  }, [form, onClose]);

  return (
    <Modal
      title="Criar documentação"
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={1000}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Row gutter={[16, 8]}>
          <Col xs={24} md={8}>
            <Form.Item label="Nome do aluno" name="search_user_name">
              <Input placeholder="Digite o nome" allowClear />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="CPF" name="by_cpf">
              <Input placeholder="Digite o CPF" allowClear />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="E-mail" name="by_user_email">
              <Input placeholder="Digite o e-mail" type="email" allowClear />
            </Form.Item>
          </Col>
        </Row>
        <Space className="w-full justify-end">
          <Button type="primary" onClick={handleSearch} loading={shouldFetch && isLoading}>
            Buscar
          </Button>
        </Space>
      </Form>

      <div className="mt-4">
        <Typography.Text strong>Resultados</Typography.Text>
        <div className="mt-2 flex flex-col gap-2">
          {enrollments.map((enrollment) => (
            <div key={enrollment.id} className="p-3 border rounded bg-neutral-50">
              <div className="flex items-start justify-between gap-6">
                <div className="grid grid-cols-2 gap-8 w-full">
                  <div className="flex flex-col">
                    <Typography.Text strong>
                      {enrollment.user?.full_name_or_name}
                    </Typography.Text>
                    <Typography.Text>E-mail: {enrollment.user?.email}</Typography.Text>
                    <Typography.Text>CPF: {enrollment.user?.personal_info?.document_info}</Typography.Text>
                  </div>
                  <div className="flex flex-col">
                    <Typography.Text strong>{enrollment.course?.name}</Typography.Text>
                    <Typography.Text>Matrícula: {enrollment.registration}</Typography.Text>
                  </div>
                </div>
                <Button
                  type="primary"
                  size="small"
                  ghost
                  onClick={() => handleAdd(enrollment.id as string)}
                  loading={creatingId === (enrollment.id as string)}
                  disabled={!!creatingId && creatingId !== (enrollment.id as string)}
                >
                  Criar documentação
                </Button>
              </div>
            </div>
          ))}
          {shouldFetch && !isLoading && enrollments.length === 0 && (
            <Typography.Text type="secondary">Nenhum resultado encontrado</Typography.Text>
          )}
        </div>
        {shouldFetch && (
          <div className="mt-4 flex justify-end">
            <Pagination
              size="small"
              current={currentPage}
              pageSize={10}
              total={enrollmentsResponse?.total ?? 0}
              onChange={(page) => setCurrentPage(page)}
              showSizeChanger={false}
              disabled={isLoading}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};


